import React, { useState } from "react";
import { Modal, Text, Button, Stack, ThemeIcon, useMantineTheme } from "@mantine/core";
import { IconRotateClockwise } from "@tabler/icons-react";
import { isMobile, isTablet } from "react-device-detect";
import useLocale from "../hooks/useLocale";
import useIsLandscape, {
  useLandscapeSupported,
  usePortraitOnly,
} from "../hooks/useOrientation";
import { landscapeEnabled } from "../utils/featureFlags";

const DISMISS_KEY = "orientationWarningDismissed";
const MODAL_Z_INDEX = 20000;

/**
 * Phone-in-landscape advisory. The app doesn't lay out well on a phone held
 * sideways, so when we detect a phone (not a tablet) in landscape we surface a
 * dismissible, theme-aware modal asking the user to rotate back to portrait.
 *
 * Self-contained: it gates on device + orientation internally so the mount
 * site (App.js) can render it unconditionally. Once dismissed it stays hidden
 * for the rest of the session (sessionStorage), even across rotations.
 *
 * The capture screens (VeratadCapture, the ID doc capture, the age-estimation
 * start drawer) render fullscreen portals on document.body at z-index
 * 9999-10001. Mantine's default Modal z-index (200) sits underneath them, so
 * without an explicit value the prompt is open but invisible until capture
 * finishes and the portal unmounts. Keep this above every capture layer and
 * below the test-mode tooling at 100000.
 *
 * Screens that lay out correctly sideways (the Veratad ID document capture)
 * register themselves via useDeclareLandscapeSupport while mounted; the
 * prompt stays hidden for them so it never covers a layout built for the
 * rotation.
 *
 * With FACE_LANDSCAPE on (?face_landscape=true) the prompt stays down
 * everywhere except on screens that declare useDeclarePortraitOnly (the
 * camera screens not built for landscape: ID doc v1, Microblink, BlinkID,
 * Mitek, the legacy DCAMS selfie, Yoti). The plan is to delete this
 * component once those have landscape layouts.
 *
 * Copy comes from the `orientation` locale namespace via useLocale, with inline
 * English fallbacks matching the defensive pattern used elsewhere in the app.
 */
const OrientationWarning = () => {
  // Phones only — tablets are explicitly out of scope.
  const isPhone = isMobile && !isTablet;

  const landscape = useIsLandscape();
  const landscapeSupported = useLandscapeSupported();
  const portraitOnly = usePortraitOnly();
  const locale = useLocale("orientation");
  const theme = useMantineTheme();

  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch (e) {
      // sessionStorage can throw in private mode / sandboxed iframes; the
      // in-memory state below still suppresses the modal for this view.
    }
    setDismissed(true);
  };

  // A portrait-only screen (ID doc v1, third-party SDK captures) gets the
  // prompt even with FACE_LANDSCAPE on, unless a screen that does lay out
  // sideways is also mounted.
  const opened =
    isPhone &&
    landscape &&
    !dismissed &&
    !landscapeSupported &&
    (portraitOnly || !landscapeEnabled());

  return (
    <Modal
      opened={opened}
      onClose={dismiss}
      centered
      withCloseButton
      closeOnClickOutside={false}
      closeOnEscape={false}
      trapFocus={false}
      padding="lg"
      zIndex={MODAL_Z_INDEX}
    >
      <Stack spacing="lg" align="center">
        <ThemeIcon size={72} radius="xl" variant="light" color={theme.primaryColor}>
          <IconRotateClockwise size={40} stroke={1.5} aria-hidden="true" />
        </ThemeIcon>
        <Stack spacing={4} align="center">
          <Text align="center" size="xl" weight={700}>
            {locale?.title || "Rotate your device"}
          </Text>
          <Text align="center">
            {locale?.message ||
              "For the best experience, please rotate your device to portrait mode."}
          </Text>
        </Stack>
        <Button fullWidth size="lg" onClick={dismiss}>
          {locale?.dismiss || "Continue anyway"}
        </Button>
      </Stack>
    </Modal>
  );
};

export default OrientationWarning;
